import React, { useState, useEffect } from 'react';
import { databaseSchemaService } from '../services/api';

function DatabaseSchema() {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('diagram'); // 'diagram' or 'table'

  useEffect(() => {
    fetchSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSchema = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await databaseSchemaService.getSchema();
      if (response.success) {
        setSchema(response.data);
        // Load Mermaid library dynamically
        loadMermaid();
      } else {
        setError(response.message || 'Error al cargar el esquema');
      }
    } catch (err) {
      console.error('Error fetching schema:', err);
      setError('Error al cargar el esquema de la base de datos');
    } finally {
      setLoading(false);
    }
  };

  const loadMermaid = () => {
    // Check if Mermaid is already loaded
    if (window.mermaid) {
      window.mermaid.initialize({ 
        startOnLoad: true,
        theme: 'default',
        er: {
          useMaxWidth: true
        }
      });
      renderDiagram();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="mermaid"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.mermaid) {
          window.mermaid.initialize({ 
            startOnLoad: true,
            theme: 'default',
            er: {
              useMaxWidth: true
            }
          });
          renderDiagram();
        }
      });
      return;
    }

    // Load Mermaid from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
    script.onload = () => {
      window.mermaid.initialize({ 
        startOnLoad: true,
        theme: 'default',
        er: {
          useMaxWidth: true
        }
      });
      renderDiagram();
    };
    document.body.appendChild(script);
  };

  const renderDiagram = () => {
    setTimeout(() => {
      if (window.mermaid) {
        window.mermaid.contentLoaded();
      }
    }, 100);
  };

  const generateMermaidERD = () => {
    if (!schema || !schema.tables) return '';

    let mermaidCode = 'erDiagram\n';

    // Add tables with their columns
    schema.tables.forEach(table => {
      const columns = table.columns
        .filter(col => !['MANY_TO_ONE', 'ONE_TO_MANY', 'MANY_TO_MANY', 'ONE_TO_ONE'].includes(col.attributeType))
        .map(col => {
          let type = col.type;
          if (col.attributeType === 'BASIC' || col.attributeType === 'EMBEDDED') {
            return `${type} ${col.name}`;
          }
          return `${type} ${col.name}`;
        });

      if (columns.length > 0) {
        mermaidCode += `    ${table.name} {\n`;
        columns.forEach(col => {
          mermaidCode += `        ${col}\n`;
        });
        mermaidCode += `    }\n`;
      }
    });

    // Add relationships
    const uniqueRelationships = new Set();
    schema.relationships.forEach(rel => {
      const key = `${rel.from}-${rel.to}`;
      if (!uniqueRelationships.has(key)) {
        uniqueRelationships.add(key);
        
        let relationSymbol = '||--o{'; // Default: one-to-many
        if (rel.type === 'MANY_TO_ONE') {
          relationSymbol = '}o--||';
        } else if (rel.type === 'MANY_TO_MANY') {
          relationSymbol = '}o--o{';
        } else if (rel.type === 'ONE_TO_ONE') {
          relationSymbol = '||--||';
        }
        
        mermaidCode += `    ${rel.from} ${relationSymbol} ${rel.to} : "${rel.attribute}"\n`;
      }
    });

    return mermaidCode;
  };

  const downloadDiagram = async () => {
    const svgElement = document.querySelector('#mermaid-diagram svg');
    if (!svgElement) {
      setError('No se pudo encontrar el diagrama para descargar');
      return;
    }

    // Get SVG content
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'database-schema.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="database-schema">
        <h2 className="page-title">Esquema de Base de Datos</h2>
        <p>Cargando esquema...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="database-schema">
        <h2 className="page-title">Esquema de Base de Datos</h2>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="database-schema">
      <h2 className="page-title">Esquema de Base de Datos</h2>
      
      <div className="schema-controls" style={{ marginBottom: '20px' }}>
        <div className="btn-group">
          <button
            className={`btn ${viewMode === 'diagram' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('diagram')}
          >
            📊 Diagrama Visual
          </button>
          <button
            className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('table')}
          >
            📋 Vista de Tablas
          </button>
        </div>
        
        {viewMode === 'diagram' && (
          <button
            className="btn btn-success"
            onClick={downloadDiagram}
            style={{ marginLeft: '10px' }}
          >
            💾 Descargar Diagrama (SVG)
          </button>
        )}
      </div>

      {viewMode === 'diagram' ? (
        <div className="diagram-container" style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '8px',
          overflow: 'auto',
          maxHeight: '800px'
        }}>
          <div id="mermaid-diagram" className="mermaid">
            {generateMermaidERD()}
          </div>
        </div>
      ) : (
        <div className="tables-view">
          <h3>Tablas de la Base de Datos ({schema?.tables?.length || 0})</h3>
          
          {schema?.tables?.map((table, index) => (
            <div key={index} className="table-card" style={{
              backgroundColor: 'white',
              padding: '15px',
              marginBottom: '15px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ color: '#2c5282', marginBottom: '10px' }}>
                {table.name}
                <span style={{ color: '#718096', fontSize: '0.9em', marginLeft: '10px' }}>
                  (Tabla: {table.tableName})
                </span>
              </h4>
              
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Columna</th>
                    <th>Tipo</th>
                    <th>Categoría</th>
                  </tr>
                </thead>
                <tbody>
                  {table.columns.map((col, colIndex) => (
                    <tr key={colIndex}>
                      <td><strong>{col.name}</strong></td>
                      <td>{col.type}</td>
                      <td>
                        <span className={`badge ${
                          col.attributeType === 'MANY_TO_ONE' || 
                          col.attributeType === 'ONE_TO_MANY' || 
                          col.attributeType === 'MANY_TO_MANY' ||
                          col.attributeType === 'ONE_TO_ONE' 
                            ? 'badge-info' 
                            : 'badge-secondary'
                        }`}>
                          {col.attributeType}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <div className="relationships-section" style={{ marginTop: '30px' }}>
            <h3>Relaciones ({schema?.relationships?.length || 0})</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Desde</th>
                  <th>Tipo</th>
                  <th>Hasta</th>
                  <th>Atributo</th>
                </tr>
              </thead>
              <tbody>
                {schema?.relationships?.map((rel, index) => (
                  <tr key={index}>
                    <td><strong>{rel.from}</strong></td>
                    <td>
                      <span className="badge badge-info">
                        {rel.type}
                      </span>
                    </td>
                    <td><strong>{rel.to}</strong></td>
                    <td>{rel.attribute}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatabaseSchema;
